import React, { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';
import { Sparkles, RotateCcw, ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';

export default function AdminPendingNumbers() {
  const { seller } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(null); // row data

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/whatsapp/numbers/pending');
      setItems(data.items || []);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Admin access required');
      else toast.error(err.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/admin/whatsapp/numbers/pending');
        if (mounted) setItems(data.items || []);
      } catch (err) {
        if (err.response?.status === 403) toast.error('Admin access required');
        else toast.error(err.response?.data?.detail || 'Failed to load');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const resetNumber = async (nid) => {
    if (!window.confirm('Reset this number? Tokens & Meta IDs will be cleared.')) return;
    try {
      await api.post(`/whatsapp/numbers/${nid}/reset`);
      toast.success('Reset OK');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  const submitProvision = async (forceTakeover = false) => {
    const p = provisioning;
    if (!p.phone_number_id || !p.access_token) return toast.error('phone_number_id and access_token required');
    try {
      const { data } = await api.post(`/admin/whatsapp/numbers/${p.id}/provision`, {
        phone_number_id: p.phone_number_id,
        waba_id: p.waba_id,
        access_token: p.access_token,
        force_takeover: forceTakeover,
      });
      toast.success(`Provisioned ${p.phone_number} ✓${data.force_takeover_applied ? ' (conflicting record archived)' : ''}`);
      setProvisioning(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured conflict — surface a "Force takeover" UI
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.conflict) {
        setProvisioning({ ...p, _conflict: detail.conflict, _conflictHint: detail.hint });
        return;
      }
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Provision failed'));
    }
  };

  if (!seller?.is_admin) {
    return (
      <div className="fab-card p-10 text-center">
        <ShieldCheck className="w-10 h-10 text-fab-text-muted mx-auto mb-2" />
        <div className="font-display font-bold text-fab-text">Admin access required</div>
        <div className="text-[13px] text-fab-text-muted mt-1">Only FABTEXS team accounts can see this page.</div>
      </div>
    );
  }

  return (
    <div className="space-y-5 fade-up">
      <div>
        <div className="font-display font-bold text-2xl text-fab-text flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-fab-primary" /> Platform Pending Numbers
        </div>
        <div className="text-[13px] text-fab-text-muted mt-1">
          Every WhatsApp number across all sellers awaiting FABTEXS-team provisioning. Paste their Meta credentials to activate.
        </div>
      </div>

      <div className="fab-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 text-[11px] uppercase tracking-wider text-fab-text-muted border-b border-fab-primary-lighter bg-fab-elevated font-semibold">
          <div className="col-span-3">Seller</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {loading && (
          <div className="p-8 flex items-center justify-center text-fab-text-muted">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-fab-text-muted" data-testid="admin-pending-empty">
            <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No pending numbers. All vendors are activated.
          </div>
        )}
        {items.map((n) => (
          <div key={n.id} className="grid grid-cols-12 px-4 py-3 border-b border-fab-primary-lighter items-center text-[13px]" data-testid={`admin-pending-row-${n.id}`}>
            <div className="col-span-3 min-w-0">
              <div className="font-semibold text-fab-text truncate">{n.seller?.business_name || n.seller?.email || '—'}</div>
              <div className="text-[11px] text-fab-text-muted truncate">{n.seller?.email}</div>
            </div>
            <div className="col-span-2">
              <span className="font-mono text-[11px] font-bold text-fab-primary">{n.seller?.plan_tier || 'FREE_TRIAL'}</span>
            </div>
            <div className="col-span-2 font-mono text-[12px]">{n.phone_number || '—'}</div>
            <div className="col-span-2">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${n.status === 'PENDING_PROVISION' ? 'bg-amber-100 text-amber-800' : 'bg-orange-100 text-orange-800'}`}>
                {n.status === 'PENDING_PROVISION' ? 'Pending' : 'Awaiting Reconnect'}
              </span>
            </div>
            <div className="col-span-3 flex justify-end gap-2">
              <button data-testid={`admin-provision-${n.id}`} onClick={() => setProvisioning({ id: n.id, phone_number: n.phone_number, seller_email: n.seller?.email, phone_number_id: '', waba_id: '', access_token: '' })}
                className="text-[11px] font-semibold px-2.5 py-1 rounded bg-fab-primary text-white hover:bg-fab-primary-dark inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Provision
              </button>
              <button data-testid={`admin-reset-${n.id}`} onClick={() => resetNumber(n.id)} className="text-[11px] font-semibold px-2.5 py-1 rounded border border-amber-300 text-amber-800 hover:bg-amber-50 inline-flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
            </div>
          </div>
        ))}
      </div>

      {provisioning && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setProvisioning(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-provision-modal">
            <div className="font-display font-bold text-fab-text text-lg mb-1">Provision Meta Credentials</div>
            <div className="text-[12px] text-fab-text-muted mb-1">For <span className="font-mono">{provisioning.phone_number}</span> · {provisioning.seller_email}</div>
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-4 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>Vendors never see these fields. AI auto-pauses on first live number (Safe-Start).</span>
            </div>
            <div className="space-y-2">
              <input data-testid="admin-prov-pnid" value={provisioning.phone_number_id} onChange={(e) => setProvisioning({ ...provisioning, phone_number_id: e.target.value })} placeholder="phone_number_id" className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono" />
              <input data-testid="admin-prov-waba" value={provisioning.waba_id} onChange={(e) => setProvisioning({ ...provisioning, waba_id: e.target.value })} placeholder="waba_id" className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono" />
              <input data-testid="admin-prov-token" type="password" value={provisioning.access_token} onChange={(e) => setProvisioning({ ...provisioning, access_token: e.target.value })} placeholder="access_token (system user)" className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono" />
            </div>

            {provisioning._conflict && (
              <div className="mt-4 p-3 rounded-lg border border-red-300 bg-red-50 text-[12px]" data-testid="admin-prov-conflict">
                <div className="font-bold text-red-800 mb-1">⚠ Phone number ID conflict</div>
                <div className="text-red-700 space-y-0.5">
                  <div>This Meta phone_number_id is already linked to another record:</div>
                  <div className="mt-1.5 pl-2 border-l-2 border-red-300">
                    <div><strong>Seller:</strong> {provisioning._conflict.seller_business || provisioning._conflict.seller_email}</div>
                    <div><strong>Email:</strong> <span className="font-mono">{provisioning._conflict.seller_email}</span></div>
                    <div><strong>Phone:</strong> <span className="font-mono">{provisioning._conflict.phone_number}</span></div>
                    <div><strong>Status:</strong> <span className="font-mono font-bold">{provisioning._conflict.status}</span></div>
                    <div><strong>Connected:</strong> <span className="font-mono">{provisioning._conflict.connected_at}</span></div>
                    <div><strong>Record id:</strong> <span className="font-mono text-[10px]">{provisioning._conflict.id}</span></div>
                  </div>
                  <div className="mt-2 text-red-800 font-semibold">"Force Takeover" archives that record and reassigns this Meta number to <span className="font-mono">{provisioning.seller_email}</span>.</div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setProvisioning(null)} className="px-4 py-2 text-[13px] text-fab-text-secondary font-semibold">Cancel</button>
              {provisioning._conflict ? (
                <button data-testid="admin-prov-force-submit" onClick={() => submitProvision(true)} className="bg-red-600 hover:bg-red-700 text-white text-[13px] font-semibold px-4 py-2 rounded-lg">Force Takeover & Activate</button>
              ) : (
                <button data-testid="admin-prov-submit" onClick={() => submitProvision(false)} className="btn-primary text-[13px]">Validate & Activate</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
